import React from 'react';
import { Bar, BarChart, XAxis, YAxis } from 'recharts';

import { cn } from '@epoch-ui/lib/utils';
import { ChartContainer, useChart, type ChartConfig } from '@epoch-ui/components/Chart';

/**
 * Customized BarChart and Bar components to get look and feel of a single horizontal stacked (segmented) progress chart.
 *
 * TODOs:
 * - Pull out the Bar component into a separate component once upgraded to recharts v3
 * - Add support for "vertical" orientation
 * - Add components to render generated chart legend and tooltip
 */

type SegmentedProgressChartData = {
  [key in string]: number;
};

type SegmentedBarProps = Omit<React.ComponentProps<typeof Bar>, 'stackId' | 'dataKey' | 'key'>;

type SegmentedProgressChartConfig = ChartConfig & {
  [k in string]: ChartConfig[k] & {
    segmentBarProps?: SegmentedBarProps;
    radiusGetter?: (index: number, total: number) => [number, number, number, number];
  };
};

interface SegmentedProgressChartProps extends Omit<React.ComponentProps<typeof BarChart>, 'data'> {
  data: SegmentedProgressChartData;
}

/**
 * Container component that wraps the chart and provides a default configuration for the chart.
 *
 * @param config.segmentBarProps - <Bar /> props for the segment bars
 * @param config.radiusGetter - Function to get the radius of the segment bars
 *
 * @see https://recharts.org/en-US/api/Bar
 */
const SegmentedProgressChartContainer = React.forwardRef<
  React.ElementRef<typeof ChartContainer>,
  React.ComponentProps<typeof ChartContainer> & {
    config: SegmentedProgressChartConfig;
  }
>(({ config, className, ...props }, ref) => {
  return (
    <ChartContainer
      ref={ref}
      config={config}
      className={cn('tw-h-2 tw-w-full [&_.recharts-surface]:tw-align-top', className)}
      {...props}
    />
  );
});
SegmentedProgressChartContainer.displayName = ChartContainer.displayName;

const DEFAULT_CHART_PROPS: Partial<SegmentedProgressChartProps> = {
  barSize: 10,
  layout: 'vertical',
  margin: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  }
};

const DEFAULT_SEGMENT_BAR_PROPS: SegmentedBarProps = {
  stroke: 'white',
  strokeWidth: 2
};

/**
 * Main component that renders the chart.
 *
 * - Accepts all props of <BarChart />
 * @see https://recharts.org/en-US/api/BarChart
 */
const SegmentedProgressChart = React.forwardRef<
  React.ElementRef<typeof BarChart>,
  SegmentedProgressChartProps
>(({ data, children, ...props }, ref) => {
  const { config } = useChart();

  // Filter out data with value 0
  const filteredData = React.useMemo(
    () => Object.entries(data).filter(([, value]) => value > 0),
    [data]
  );
  // Calculate the total value of the filtered data
  const dataTotal = React.useMemo(
    () => filteredData.reduce((acc, [, value]) => acc + value, 0),
    [filteredData]
  );

  // Calculate count of non-zero data points
  const totalSegments = React.useMemo(() => {
    return Object.keys(filteredData).length;
  }, [filteredData]);

  return (
    <BarChart ref={ref} {...DEFAULT_CHART_PROPS} data={[data]} {...props}>
      <XAxis type="number" hide domain={[0, dataTotal]} />
      <YAxis type="category" dataKey="name" hide />
      {filteredData.map(([key], index) => {
        const { segmentBarProps = DEFAULT_SEGMENT_BAR_PROPS, ...itemConfig } = (config[
          key
        ] as SegmentedProgressChartConfig[string]) ?? {
          color: '#0071E6'
        };

        return (
          // TODO: Pull this out into separate component once upgraded to recharts v3
          <Bar
            key={key}
            stackId="a"
            dataKey={key}
            fill={itemConfig.color}
            radius={
              itemConfig.radiusGetter?.(index, totalSegments) ??
              fullRoundedSides(index, totalSegments)
            }
            {...(segmentBarProps as any)}
          />
        );
      })}
      {children}
    </BarChart>
  );
});
SegmentedProgressChart.displayName = 'SegmentedProgressChart';

// Utility for calculating bar radius based on position
const FULL_BAR_RADIUS = 9999;

const fullRoundedSides = (
  index: number,
  totalSegments: number
): [number, number, number, number] => {
  // Radius array is in the order of top-left, top-right, bottom-right, bottom-left

  if (totalSegments === 1) {
    // 1 segment - both sides are rounded
    return [FULL_BAR_RADIUS, FULL_BAR_RADIUS, FULL_BAR_RADIUS, FULL_BAR_RADIUS];
  } else if (index === 0) {
    // First segment - top-left and bottom-left are rounded
    return [FULL_BAR_RADIUS, 0, 0, FULL_BAR_RADIUS];
  } else if (index === totalSegments - 1) {
    // Last segment - top-right and bottom-right are rounded
    return [0, FULL_BAR_RADIUS, FULL_BAR_RADIUS, 0];
  }

  // Middle segments - no sides are rounded
  return [0, 0, 0, 0];
};

export {
  SegmentedProgressChartContainer,
  SegmentedProgressChart,
  type SegmentedProgressChartData,
  type SegmentedProgressChartConfig
};
